import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Palette, Upload, FileText, Send, CheckCircle, Clock, XCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../services/api';

function CreatorApplicationPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [formData, setFormData] = useState({
    portfolio_url: '',
    sample_work_urls: '',
    experience: '',
    why_join: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if already a creator
    if (user?.subscription_tier === 'creator') {
      toast.info('You are already a Creator!');
      navigate('/dashboard');
      return;
    }

    // Check application status
    checkApplicationStatus();
  }, [isAuthenticated, user, navigate]);

  const checkApplicationStatus = async () => {
    try {
      const res = await api.get('/api/subscriptions/creator-application/status');
      setApplicationStatus(res.data);
    } catch (error) {
      console.error('Error checking application status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.experience.trim() || !formData.why_join.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        portfolio_url: formData.portfolio_url || null,
        sample_work_urls: formData.sample_work_urls ? formData.sample_work_urls.split('\n').filter(url => url.trim()) : [],
        experience: formData.experience,
        why_join: formData.why_join
      };

      await api.post('/api/subscriptions/creator-application', payload);
      toast.success('Application submitted successfully!');
      setApplicationStatus({ has_application: true, status: 'pending' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-hot-rod border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show status if application exists
  if (applicationStatus?.has_application) {
    const status = applicationStatus.status;
    return (
      <div className="min-h-screen pt-16 pb-24 page-padding" data-testid="creator-application-status">
        <div className="max-w-2xl mx-auto">
          <Link to="/pricing" className="inline-flex items-center gap-2 text-text-secondary hover:text-white mb-8">
            <ArrowLeft size={20} />
            Back to Pricing
          </Link>

          <div className="card-hotrod p-8 text-center">
            {status === 'pending' && (
              <>
                <Clock className="mx-auto text-yellow-500 mb-4" size={64} />
                <h1 className="font-teko text-4xl mb-4">Application Under Review</h1>
                <p className="text-text-secondary text-lg mb-6">
                  Thanks for applying! Our team is reviewing your application.
                  We'll notify you via email within 48 hours.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 rounded-lg text-yellow-400">
                  <Clock size={20} />
                  <span>Pending Review</span>
                </div>
              </>
            )}
            
            {status === 'approved' && (
              <>
                <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                <h1 className="font-teko text-4xl mb-4 text-green-400">Application Approved!</h1>
                <p className="text-text-secondary text-lg mb-6">
                  Welcome to the Creator family! You now have access to all creator tools.
                </p>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="btn-hotrod"
                >
                  Go to Dashboard
                </button>
              </>
            )}
            
            {status === 'rejected' && (
              <>
                <XCircle className="mx-auto text-red-500 mb-4" size={64} />
                <h1 className="font-teko text-4xl mb-4 text-red-400">Application Not Approved</h1>
                <p className="text-text-secondary text-lg mb-6">
                  Unfortunately, your application wasn't approved at this time.
                  Feel free to reach out to our support team for feedback.
                </p>
                <Link to="/customer-service" className="btn-outline">
                  Contact Support
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show application form
  return (
    <div className="min-h-screen pt-16 pb-24 page-padding" data-testid="creator-application-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/pricing" className="inline-flex items-center gap-2 text-text-secondary hover:text-white mb-8">
          <ArrowLeft size={20} />
          Back to Pricing
        </Link>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
            <Palette className="text-white" size={40} />
          </div>
          <h1 className="font-teko text-5xl text-gradient mb-2">Become a Creator</h1>
          <p className="text-text-secondary text-lg">
            Share your stories with the world and earn from your passion
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-2 gap-4 mb-10">
          <div className="card-hotrod p-4 text-center">
            <Upload className="mx-auto text-purple-400 mb-2" size={28} />
            <p className="text-sm text-text-secondary">Publish Comics</p>
          </div>
          <div className="card-hotrod p-4 text-center">
            <FileText className="mx-auto text-purple-400 mb-2" size={28} />
            <p className="text-sm text-text-secondary">Analytics Dashboard</p>
          </div>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="card-hotrod p-8 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">
              Portfolio URL <span className="text-text-secondary">(optional)</span>
            </label>
            <input
              type="url"
              value={formData.portfolio_url}
              onChange={(e) => setFormData({...formData, portfolio_url: e.target.value})}
              placeholder="https://your-portfolio.com"
              className="w-full px-4 py-3 bg-background border border-surface-light rounded-lg focus:outline-none focus:border-purple-500"
              data-testid="portfolio-url-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Sample Work URLs <span className="text-text-secondary">(one per line, optional)</span>
            </label>
            <textarea
              value={formData.sample_work_urls}
              onChange={(e) => setFormData({...formData, sample_work_urls: e.target.value})}
              placeholder="https://example.com/artwork1&#10;https://example.com/artwork2"
              rows={3}
              className="w-full px-4 py-3 bg-background border border-surface-light rounded-lg focus:outline-none focus:border-purple-500 resize-none"
              data-testid="sample-urls-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Your Experience <span className="text-hot-rod">*</span>
            </label>
            <textarea
              value={formData.experience}
              onChange={(e) => setFormData({...formData, experience: e.target.value})}
              placeholder="Tell us about your experience with comics, manga, or illustration..."
              rows={4}
              required
              className="w-full px-4 py-3 bg-background border border-surface-light rounded-lg focus:outline-none focus:border-purple-500 resize-none"
              data-testid="experience-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Why do you want to join Supremics? <span className="text-hot-rod">*</span>
            </label>
            <textarea
              value={formData.why_join}
              onChange={(e) => setFormData({...formData, why_join: e.target.value})}
              placeholder="What excites you about creating on our platform..."
              rows={4}
              required
              className="w-full px-4 py-3 bg-background border border-surface-light rounded-lg focus:outline-none focus:border-purple-500 resize-none"
              data-testid="why-join-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 px-6 rounded-lg font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-application-btn"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send size={20} />
                <span>Submit Application</span>
              </>
            )}
          </button>
        </form>

        <p className="text-center text-text-secondary text-sm mt-6">
          Applications are typically reviewed within 48 hours.
          We'll notify you via email once a decision is made.
        </p>
      </div>
    </div>
  );
}

export default CreatorApplicationPage;
